import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  QueryList,
  Renderer2,
  SimpleChanges,
  ViewChildren,
  NgZone
} from '@angular/core'
import {
  combineLatest,
  interval,
  fromEvent,
  merge,
  of,
  Observable,
  ReplaySubject,
  Subject,
  Scheduler,
  BehaviorSubject,
  concat,
  EMPTY,
  timer
} from 'rxjs'
import {
  filter,
  flatMap,
  map,
  share,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
  delay,
  takeWhile,
  distinctUntilChanged,
  take,
  mapTo,
  switchMapTo
} from 'rxjs/operators'
import { PreviewService } from '../../services/preview.service'
import { CustomEventHandler, ListenerDictionary } from './video-player.events'
import { IdGeneratorService } from '../../../singleton-services/id-generator.service'
import { PlatformService } from '../../../singleton-services/platform.service'
import { IVideoAnalyticsData } from '../../models/video-analytics-data.model'
import { maybe, IMaybe } from 'typescript-monads'
import { IVolumeControls } from '../../models'
import { VideoPlayerControlsPlayToggleClickEvent } from '../video-player-controls/video-player-controls.interfaces'
import { VideoPlayerScreenErrorCode } from '../../../shared/video-player/models/video-player-screen-error-code.model'
import { IVideoSkip } from '../video-player-controls/video-player-controls.component'
import { youboraCdns } from '../../../shared/analytics/models/youbora-cdns'
import { AdBlockService } from '@flosportsinc/ng-ad-block'
import { DOCUMENT } from '@angular/common'
import { UserAgentService } from '../../../singleton-services/user-agent.service'
import {
  ViewMode,
  ErrorInterface,
  IVideoQuality,
  IVideoScrubber,
  HlsLoaderResponse,
  uuidPerTabGenFn,
  HlsLoader
} from './video-player.interfaces'
import {
  VIDEO_PLAYER_EVENT_UUID_TAB_GENERATOR,
  VIDEO_PLAYER_EVENT_BINDINGS,
  VIDEO_PLAYER_HLS_URL_LOADER
} from './video-player.tokens'
import Hls from 'hls.js'
import { RXJS_SCHEDULER } from '../../../app.config'
import { VideoPlayerMeta } from '../../models/stream-tile'
import {
  IVideoLoggingErrorValueType,
  IVideoLoggingInfoValue,
  VideoEventLoggingService
} from '../../services/video-event-logging.service'
import { LiveEventStatus } from 'src/app/events/events.enum'
import { getHlsQualityLevels } from '../../functions/utility-functions'
import {
  buildRenditionString,
  getBitrateFromHls
} from 'src/app/shared/video-player/components/video-player/video-player.utility'
import { WINDOW } from 'src/app/app.injections'
import { VideoAdsService } from '../../services/video-ads.service'

function isFunction(obj: any) {
  return (b: string) => {
    return typeof obj[b] === 'function'
  }
}

function setPlayerId(renderer: Renderer2, videoElement: HTMLVideoElement, id: string): void {
  renderer.setAttribute(videoElement, 'ID', id)
}

export const dispatchCustomEvent = (doc: HTMLDocument, str: string, videoElement: HTMLVideoElement, detail: any) => {
  try {
    videoElement.dispatchEvent(new CustomEvent(str, { detail }))
  } catch (err) {
    const event = doc.createEvent('CustomEvent')
    event.initCustomEvent(str, false, false, detail)
    videoElement.dispatchEvent(event)
  }
}

export const mapGetResolution = (source: Observable<any>) =>
  source.pipe(
    map(([manifestParsedEvent, levelSwitchedEvent]) =>
      maybe(manifestParsedEvent.levels[levelSwitchedEvent.level])
        .flatMapAuto(a => a.attrs)
        .flatMapAuto(a => a.RESOLUTION)
        .valueOrUndefined()
    )
  )

const mapServiceErrorsToVideoErrors = (serviceErrorCode: number) => {
  switch (serviceErrorCode) {
    case 0:
      return VideoPlayerScreenErrorCode.FATAL_HLS
    case 401:
      return VideoPlayerScreenErrorCode.AUTHENTICATION_REQUIRED
    case 402:
      return VideoPlayerScreenErrorCode.UNAUTHORIZED
    case 403:
      return VideoPlayerScreenErrorCode.FORBIDDEN
    case 404:
      return VideoPlayerScreenErrorCode.STREAM_NOT_FOUND
    case 422:
      return VideoPlayerScreenErrorCode.STREAM_NOT_FOUND
    case 451:
      return VideoPlayerScreenErrorCode.GEO_UNAVAILABLE
    default:
      return VideoPlayerScreenErrorCode.UNKNOWN_ERROR
  }
}

interface RenderedVideoPlayerComponent {
  view$: Observable<{
    mode: ViewMode
  }>
}

@Component({
  selector: 'flo-video',
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VideoPlayerComponent implements AfterViewInit, OnChanges, OnDestroy, RenderedVideoPlayerComponent {
  @Input()
  set videoAnalyticsData(videoAnalyticsData: IVideoAnalyticsData) {
    if (videoAnalyticsData) {
      this.videoAnalyticsDataSource.next(videoAnalyticsData)
    }
  }

  @Input()
  get videoMeta() {
    return this._videoMeta
  }

  set videoMeta(val: VideoPlayerMeta) {
    this._videoMeta = val
    this.videoMetaSource.next(this._videoMeta)
  }

  @Input()
  set updateLivePlayHead(updatePlayHead: boolean) {
    this.updateLivePlayHeadSource.next(updatePlayHead)
  }

  @Input()
  set eventStatus(eventStatus: LiveEventStatus) {
    this.eventStatusSource.next(eventStatus)
  }

  @Input()
  set enablePrerollAds(value: boolean) {
    this.enablePrerollAdsSource.next(value)
  }

  constructor(
    private elm: ElementRef,
    private prev: PreviewService,
    private is: IdGeneratorService,
    private rd: Renderer2,
    private ps: PlatformService,
    private ua: UserAgentService,
    private ads: AdBlockService,
    private zone: NgZone,
    private videoEventLoggingService: VideoEventLoggingService,
    private readonly videoAdsService: VideoAdsService,
    @Inject(DOCUMENT) private doc: Document,
    @Inject(VIDEO_PLAYER_EVENT_UUID_TAB_GENERATOR) private getWindowGuidFn: uuidPerTabGenFn,
    @Inject(VIDEO_PLAYER_HLS_URL_LOADER) private hlsLoader: HlsLoader,
    @Inject(RXJS_SCHEDULER) private scheduler: Scheduler,
    @Inject(WINDOW) private readonly window: Window,
    @Optional()
    @Inject(VIDEO_PLAYER_EVENT_BINDINGS)
    private videoEventBindings?: ReadonlyArray<ListenerDictionary>
  ) {
    setPlayerId(this.rd, this.elm.nativeElement, this.id.toString())
    combineLatest([this.videoReferenceId$, this.isFreeStream$, this.videoMeta$, this.eventStatus$])
      .pipe(
        map(a => ({ videoReferenceId: a[0], isFreeStream: a[1], videoMeta: a[2], eventStatus: a[3] })),
        distinctUntilChanged((x, y) => {
          return (
            x.videoReferenceId === y.videoReferenceId &&
            x.isFreeStream === y.isFreeStream &&
            x.videoMeta === y.videoMeta &&
            x.eventStatus === y.eventStatus
          )
        }),
        map(({ videoReferenceId, isFreeStream, videoMeta, eventStatus }) => ({
          videoReferenceId,
          isFreeStream,
          videoMeta: { ...videoMeta, eventStatus }
        })),
        tap(videoInfo => this.newTokenRequest$.next(videoInfo)),
        switchMap(obj => this.hlsLoader(obj.videoReferenceId, obj.isFreeStream, obj.videoMeta)),
        takeUntil(this.ngOnDestroy$)
      )
      .subscribe(hls => this.hlsSource.next(hls))
    this.hls$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(hlsData => {
      // hls stream has been resolved, video element is now defined
      setTimeout(() => {
        maybe(hlsData.error)
          .map(err => ({ code: err.code, message: err.message, meta: err.meta }))
          .tapSome(err => this.errorSource.next(err))

        this.prepareAndRemoveListeners()
        maybe(hlsData.success)
          .map(success => success.meta)
          .tapSome(meta => this.prepareAndAddListeners(meta))
      })
    })
  }

  public static readonly LATEST_CHUNKLIST_LENGTH = 6
  private readonly videoAnalyticsDataSource = new ReplaySubject<IVideoAnalyticsData>(1)
  public readonly floMseConfig: ReadonlyArray<any> = [{ execKey: 'HLS', config: { capLevelToPlayerSize: true } }]

  @HostBinding('style.height') height = '100%'
  @HostBinding('style.width') width = '100%'
  @ViewChildren('videoElement') videoElement?: QueryList<ElementRef<HTMLVideoElement>>
  @Input() videoReferenceId: string
  @Input() isFreeStream: boolean
  @Input() skipTrackingPreview = false
  @Input() autoplay = false
  @Input() error?: ErrorInterface
  @Input() isTbd?: boolean
  @Input() isSelectedVideo?: boolean
  @Input() public readonly volumeControls: IVolumeControls
  @Input() public readonly qualityLevel: number
  @Input() public readonly playToggle: VideoPlayerControlsPlayToggleClickEvent
  @Input() public readonly pauseVideoOnSeeking: boolean
  @Input() public readonly videoPositionChange: number
  @Input() public readonly videoSkipForward: number
  @Input() public readonly videoSkipBackward: number
  @Input() public enableTattoo = false
  private _videoMeta: VideoPlayerMeta

  @Output() readonly videoLoaded: EventEmitter<HTMLVideoElement> = new EventEmitter()
  @Output() readonly hlsLoaded: EventEmitter<IVideoQuality> = new EventEmitter()
  @Output() readonly playState: EventEmitter<VideoPlayerControlsPlayToggleClickEvent> = new EventEmitter()
  @Output() readonly videoDuration: EventEmitter<number> = new EventEmitter()
  @Output() readonly videoCurrentTime: EventEmitter<IVideoScrubber> = new EventEmitter()
  @Output() readonly videoBufferAmount: EventEmitter<number> = new EventEmitter()
  @Output() readonly volumeChange: EventEmitter<IVolumeControls> = new EventEmitter()
  @Output() readonly adPlaying: EventEmitter<boolean> = new EventEmitter()

  private readonly hlsInstanceSource = new BehaviorSubject<IMaybe<Hls>>(maybe<Hls>())
  public readonly hlsInstance$ = this.hlsInstanceSource.asObservable()

  private fatalRetryCounterSource = new BehaviorSubject<number>(0)

  private MAX_HLS_FATAL_RETRY_CYCLES = 3

  public readonly tattooStyle$ = this.ps.isServer
    ? EMPTY
    : this.zone.runOutsideAngular(() =>
        interval(3000, this.scheduler).pipe(
          map((tick: number) => {
            if (this.enableTattoo) {
              const x = Math.floor(Math.random() * 5)
              const y = Math.floor(Math.random() * 5)
              const horizontal = tick % 5 > 2 ? { left: `${x}em` } : { right: `${y}em` }
              const vertical = tick % 3 > 1 ? { top: `${x}em` } : { bottom: `${y}em` }
              return tick % 3 > 1 && { ...horizontal, ...vertical }
            } else {
              return false
            }
          })
        )
      )

  public id = this.is.getUniqueId()
  public vpsErrorCode = VideoPlayerScreenErrorCode
  public groupId = this.getWindowGuidFn()
  private listRefMap: { [key: string]: CustomEventHandler<Event, any, any> } = {}

  private spinnerSource = new Subject<boolean>()
  private newTokenRequest$ = new Subject<IVideoLoggingInfoValue>()
  private errorSource = new Subject<ErrorInterface | undefined>()
  private errors$ = this.errorSource.pipe(
    filter<ErrorInterface>(Boolean),
    startWith({} as ErrorInterface),
    filter(a => a.message !== 'OK'), // WTF? iOS battery saver mode throws an error "OK"....?
    tap(err => {
      maybe(err)
        .filter(e => e.code === VideoPlayerScreenErrorCode.FATAL_HLS)
        .tapSome(() => this.runRecover().subscribe())
    }),
    map(err => {
      return err && Object.keys(err).length
        ? err.code === VideoPlayerScreenErrorCode.UNKNOWN_ERROR
          ? { ...err, message: 'An error occurred' }
          : err
        : undefined
    }),
    share()
  )

  private videoSkipForwardSource = new Subject<IVideoSkip>()
  private videoSkipBackwardSource = new Subject<IVideoSkip>()
  private isSeekingSource = new Subject<boolean>()
  private videoPositionSource = new Subject<number>()
  private videoReferenceIdSource = new Subject<string>()
  private isFreeStreamSource = new Subject<boolean>()
  private isSelectedVideoSource = new Subject<boolean>()
  private previewInteruptSource = new Subject<void>()
  private videoMetaSource = new Subject<VideoPlayerMeta>()
  private updateLivePlayHeadSource = new Subject<boolean>()
  private previewCountdownSource = new Subject<{
    showCountdown: boolean
    secondsRemaining: number
  }>()
  private enablePrerollAdsSource = new BehaviorSubject<boolean>(false)
  public analyticsPluginSource = new ReplaySubject<any>(1)
  public hlsLevelSwitchedEvent = new ReplaySubject<any>(1)
  public hlsManifestParsedEvent = new ReplaySubject<any>(1)
  public eventStatusSource = new ReplaySubject<LiveEventStatus>(1)

  private previewCountdown$ = this.previewCountdownSource.pipe(
    startWith({ showCountdown: false, secondsRemaining: 0 }),
    shareReplay(1)
  )
  analyticsPlugin$ = this.analyticsPluginSource
  videoReferenceId$ = this.videoReferenceIdSource.pipe(shareReplay(1))
  videoMeta$ = this.videoMetaSource.pipe(shareReplay(1))
  eventStatus$ = this.eventStatusSource.pipe(shareReplay(1))
  isFreeStream$ = this.isFreeStreamSource.pipe(shareReplay(1))
  isSelectedVideo$ = this.isSelectedVideoSource
  videoSkipForward$ = this.videoSkipForwardSource.pipe(shareReplay(1))
  videoSkipBackward$ = this.videoSkipBackwardSource.pipe(shareReplay(1))
  updateLivePlayHead$ = this.updateLivePlayHeadSource.pipe(shareReplay(1))
  private hlsSource = new Subject<HlsLoaderResponse<any>>()
  private hls$ = this.hlsSource.pipe(shareReplay(1))
  private enablePrerollAds$ = this.enablePrerollAdsSource

  // TODO: Lift this into external factory
  listeners: ListenerDictionary = {
    loadedmetadata: () => this.stopSpinner(),
    canplay: () => this.stopSpinner(),
    canplaythrough: () => this.stopSpinner(),
    pause: () => this.pausePreviewTracking(),
    waiting: () => this.startSpinner(),
    play: () => {
      if (!this.skipTrackingPreview) {
        this.prev
          .beginTrackingViewTime(this.videoReferenceId, this.previewInteruptSource)
          .pipe(
            tap(a =>
              this.previewCountdownSource.next({
                showCountdown: a.secondsRemaining > 0,
                secondsRemaining: a.secondsRemaining
              })
            ),
            filter(a => !a.canPreview)
          )
          .subscribe(() => {
            this.videoReferenceIdSource.next(this.videoReferenceId)
            this.isFreeStreamSource.next(this.isFreeStream)
          })
      }
    }
  }

  public readonly isLiveStream$ = this.videoMeta$.pipe(
    map(video => video?.eventStatus === LiveEventStatus.Live),
    distinctUntilChanged()
  )

  public readonly enablePreroll$ = combineLatest([
    this.videoAdsService.canLoadPrerollAds$,
    this.isLiveStream$,
    this.enablePrerollAds$
  ]).pipe(
    map(([canLoadPrerollAds, isLive, enableInput]) => {
      return canLoadPrerollAds && isLive && enableInput
    }),
    distinctUntilChanged()
  )

  public readonly videoLoaded$ = this.videoLoaded.pipe(shareReplay(1))

  public readonly videoAnalyticsData$ = this.videoLoaded$.pipe(
    switchMap(contentVideoElement => fromEvent(contentVideoElement, 'resize').pipe(map(() => contentVideoElement))),
    withLatestFrom(this.videoAnalyticsDataSource, this.hls$, this.hlsInstance$),
    map(([video, videoAnalyticsData, hlsData, hlsInstance]) => {
      const _hls = hlsInstance.valueOrUndefined()
      const bitrate = _hls ? getBitrateFromHls(_hls) : undefined
      const rendition = buildRenditionString(video.videoWidth, video.videoHeight, bitrate)
      const edge = hlsData.success && hlsData.success.hlsUrl
      const streamName =
        hlsData.success && hlsData.success.meta && hlsData.success.meta.stream && hlsData.success.meta.stream.name
      const cdnNameIndex = youboraCdns
        .map(youbora => youbora.name)
        .map(name => name.toLowerCase().replace(' ', ''))
        .findIndex(name => edge && edge.indexOf(name) > -1)
      const cdn = (youboraCdns[cdnNameIndex] || { name: undefined }).name
      const username = videoAnalyticsData.username || '0'
      return {
        ...videoAnalyticsData,
        username,
        'content.rendition': rendition,
        'content.cdn': cdn,
        'content.title': `${videoAnalyticsData['content.title']} | ${streamName}`,
        'content.resource': edge
      }
    }),
    shareReplay(1)
  )

  private readonly ngOnDestroy$ = new Subject<void>()

  readonly isPageHiddenSub = this.ps.isServer
    ? of(false)
    : concat(
        fromEvent<Event>(this.doc as HTMLDocument, 'visibilitychange'),
        fromEvent<Event>(this.doc as HTMLDocument, 'msvisibilitychange'),
        fromEvent<Event>(this.doc as HTMLDocument, 'webkitvisibilitychange')
      )
        .pipe(
          map(evt =>
            maybe(evt.target as HTMLDocument)
              .map(a => a.hidden)
              .valueOrUndefined()
          ),
          filter<boolean>(a => a !== undefined),
          takeUntil(this.ngOnDestroy$)
        )
        .subscribe(isHidden => {
          // handle mobile devices backgrounding by halting videos and reloading on re-hydration
          // A more elegant solution will be implemented inside the upciming flo-video player rewrite
          this.maybeVideoElement()
            .filter(() => this.ua.userAgent.isMobile === true)
            .tapSome(videoElement => {
              if (isHidden) {
                this.cleanupVideo()
                videoElement.src = '' // stop <video> from downloading more HLS data. Is there another way?
                videoElement.hidden = true
              } else {
                this.startSpinner()
                combineLatest([this.runRecover(), fromEvent(videoElement, 'loadedmetadata')])
                  .pipe(take(1))
                  .subscribe(() => {
                    videoElement.hidden = false
                    this.stopSpinner()
                  })
              }
            })
        })

  view$ = combineLatest([this.errors$, this.hls$, this.previewCountdown$, this.eventStatus$]).pipe(
    map(([error, hlsLoader, countdown, eventStatus]) => {
      return {
        mode: error && error.code !== VideoPlayerScreenErrorCode.FATAL_HLS ? ViewMode.ERROR : ViewMode.PLAYER,
        error,
        hlsLoader,
        countdown,
        eventIsConcluded: eventStatus === 'CONCLUDED'
      }
    }),
    shareReplay(1)
  )

  private readonly showingClickToPlayBtnSource = new Subject<boolean>()
  public readonly showingClickToPlayBtn$ = this.showingClickToPlayBtnSource.pipe(startWith(false), share())

  showSpinner$ = combineLatest([
    this.spinnerSource.pipe(startWith(true), shareReplay(1)),
    this.view$,
    this.showingClickToPlayBtn$
  ]).pipe(map(([spin, view, showingClickToPlayBtn]) => spin && !showingClickToPlayBtn && view.mode !== ViewMode.ERROR))

  private volumeSource = new Subject<IVolumeControls>()
  public volumeControls$ = combineLatest([this.volumeSource, this.isSelectedVideo$]).pipe(
    map(([volume, isSelectedStream]) => ({
      ...volume,
      isSelectedStream
    })),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  private qualitySource = new Subject<number>()
  private playToggleSource = new Subject<boolean>()
  public adEventSource = new Subject<string>()
  public adEvent$ = this.adEventSource
  private qualityLevel$ = this.qualitySource.pipe(
    withLatestFrom(this.isSelectedVideo$),
    map(([quality, isSelectedStream]) => ({
      quality,
      isSelectedStream
    })),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  private playToggle$ = this.playToggleSource.pipe(
    withLatestFrom(this.isSelectedVideo$),
    map(([isPlaying, isSelectedStream]) => ({
      isPlaying,
      isSelectedStream
    })),
    shareReplay(1),
    takeUntil(this.ngOnDestroy$)
  )

  // used when video loads and is playing to change svg to play icon
  public readonly videoPlaying$ = combineLatest([this.isSelectedVideo$, this.videoLoaded$])
    .pipe(
      map(([isSelectedVideo, videoElement]) => ({
        isSelectedVideo,
        videoElement
      })),
      filter(res => res.isSelectedVideo),
      flatMap(res => merge(fromEvent(res.videoElement, 'play'), fromEvent(res.videoElement, 'pause'))),
      map(event => event.srcElement),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe((videoElement: HTMLVideoElement) => this.playState.next({ isPaused: videoElement.paused }))

  // listens to the time of the video and updates the scrubber with the currentTime
  public updateScrubber$ = combineLatest([this.isSelectedVideo$, this.videoLoaded$])
    .pipe(
      map(([isSelectedVideo, videoElement]) => ({
        isSelectedVideo,
        videoElement
      })),
      flatMap(res =>
        merge(
          fromEvent(res.videoElement, 'timeupdate'),
          fromEvent(res.videoElement, 'seeking'),
          fromEvent(res.videoElement, 'durationchange')
        )
      ),
      map(event => event.srcElement),
      filter(res => !!res && !!this.isSelectedVideo),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe((srcElement: HTMLVideoElement) => {
      // Checking if the currentTime of the video is within 10 seconds of the available stream. If so then they are at the
      // live playhead.
      // subtracting latest chunklist from the total available duration so users can't scrub to the end of the video
      // and have it wait to catch up to the next chunklist.
      const isAtLiveHead =
        srcElement.duration - srcElement.currentTime <= VideoPlayerComponent.LATEST_CHUNKLIST_LENGTH * 4
      this.videoCurrentTime.next({
        currentTime: srcElement.currentTime,
        duration: srcElement.duration - VideoPlayerComponent.LATEST_CHUNKLIST_LENGTH,
        isAtLiveHead
      })
    })

  public videoVolumeChangeEvent$ = this.videoLoaded$.pipe(
    switchMap(video => fromEvent(video, 'volumechange')),
    map(event => event.srcElement),
    filter(() => !!this.isSelectedVideo),
    map((videoElement: HTMLVideoElement) => ({
      volume: videoElement.volume,
      isMuted: videoElement.muted,
      isSelectedStream: this.isSelectedVideo
    }))
  )

  public goToLivePlayHead = this.updateLivePlayHead$
    .pipe(
      withLatestFrom(this.videoLoaded$),
      filter(() => !!this.isSelectedVideo),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(([updatePlayHead, videoElement]) => {
      // subtracting latest chunklist from the total available duration so users can't scrub to the end of the video
      // and have it wait to catch up to the next chunklist.
      videoElement.currentTime = videoElement.duration - VideoPlayerComponent.LATEST_CHUNKLIST_LENGTH
    })

  // Updates to the scrubber to provide the user with a visual for how much of the video is available/buffered
  public updateBuffer$ = combineLatest([this.isSelectedVideo$, this.videoLoaded$])
    .pipe(
      map(([isSelectedVideo, videoElement]) => ({
        isSelectedVideo,
        videoElement
      })),
      flatMap(res => fromEvent(res.videoElement, 'progress')),
      map(event => event.srcElement),
      filter(
        (videoElement: HTMLVideoElement) =>
          !!videoElement && !!this.isSelectedVideo && videoElement.readyState > 1 && !!videoElement.buffered.length
      ),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe((srcElement: HTMLVideoElement) => {
      const bufferTime = (srcElement.buffered.end(srcElement.buffered.length - 1) / srcElement.duration) * 100
      this.videoBufferAmount.next(bufferTime)
    })

  // when user seeking with scrubber, pause the video and update the paused video with current position
  public isSeeking$ = combineLatest([this.videoLoaded, this.isSeekingSource])
    .pipe(
      filter<any>(() => !!this.isSelectedVideo),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(([videoElement, isSeeking]: readonly [HTMLVideoElement, any]) => {
      videoElement.pause()
      videoElement.currentTime = isSeeking.position
    })

  // when user selects video position, play and update the currentTime with videoPosition
  public videoPosition$ = combineLatest([this.videoLoaded, this.videoPositionSource])
    .pipe(
      filter<any>(() => !!this.isSelectedVideo),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(([videoElement, videoPosition]: readonly [HTMLVideoElement, number]) => {
      videoElement.currentTime = videoPosition
      if (!this.playToggle || !this.playToggle.isPaused) {
        videoElement.play().then(() => {
          this.hideClickToPlayBtn()
        })
      }
    })

  // User hit back or forward 10 second svg
  public videoSkipped$ = combineLatest([this.videoLoaded, merge(this.videoSkipBackward$, this.videoSkipForward$)])
    .pipe(
      filter<any>(() => !!this.isSelectedVideo),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(([videoElement, videoSkip]: readonly [HTMLVideoElement, IVideoSkip]) => {
      videoElement.currentTime = videoElement.currentTime + videoSkip.skipTime
    })

  public readonly play = combineLatest([this.videoLoaded$, this.enablePreroll$])
    .pipe(takeUntil(this.ngOnDestroy$))
    .subscribe(([contentVideoElement, enablePreroll]: [HTMLVideoElement, boolean]) => {
      if (enablePreroll) {
        // Hide and mute video before ad
        contentVideoElement.muted = true
        contentVideoElement.style.visibility = 'hidden'
        this.volumeChange.next({ isMuted: true, volume: contentVideoElement.volume })

        // Wait for ad to complete
        this.adComplete$.pipe(take(1)).subscribe(() => {
          contentVideoElement.style.visibility = 'visible'

          // Try playing the video
          contentVideoElement
            .play()
            .then(() => {
              this.hideClickToPlayBtn()
              this.updateLivePlayHeadSource.next(true)
            })
            .catch(() => {
              contentVideoElement.muted = true
              this.volumeChange.next({ isMuted: true, volume: contentVideoElement.volume })
              contentVideoElement
                .play()
                .then(() => this.hideClickToPlayBtn())
                .catch(() => {
                  contentVideoElement.muted = false
                  contentVideoElement.volume = 1
                  this.showClickToPlayBtn()
                  this.volumeChange.next({ isMuted: false, volume: contentVideoElement.volume })
                })
            })
        })
      } else {
        // No preroll - play immediately
        contentVideoElement
          .play()
          .then(() => this.hideClickToPlayBtn())
          .catch(() => {
            contentVideoElement.muted = true
            this.volumeChange.next({ isMuted: true, volume: contentVideoElement.volume })
            contentVideoElement
              .play()
              .then(() => this.hideClickToPlayBtn())
              .catch(() => {
                contentVideoElement.muted = false
                contentVideoElement.volume = 1
                this.showClickToPlayBtn()
                this.volumeChange.next({ isMuted: false, volume: contentVideoElement.volume })
              })
          })
      }
    })

  // listen to duration and currentTime being loaded. When they do put the currentTime in the live position
  // if in live status put them to the front of the stream otherwise start the dvr stream from the beginning

  public readonly initialPlay$ = this.videoLoaded$
    .pipe(
      switchMap(video => fromEvent(video, 'durationchange')),
      map(event => event.srcElement),
      filter<HTMLVideoElement>(Boolean),
      map(srcElement => ({ srcElement, id: srcElement.id })),
      distinctUntilChanged((x, y) => x.id === y.id),
      filter(Boolean),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(({ srcElement }) => {
      if (this.videoMeta && this.videoMeta.eventStatus !== LiveEventStatus.Live) srcElement.currentTime = 0
    })

  public readonly setVolume = combineLatest([
    this.videoLoaded$,
    merge(this.volumeControls$).pipe(
      distinctUntilChanged((x, y) => {
        return (
          x.volume === y.volume &&
          x.isMuted === y.isMuted &&
          x.isSelectedStream === y.isSelectedStream &&
          x.selectedStreamId === y.selectedStreamId
        )
      }),
      takeUntil(this.ngOnDestroy$)
    )
  ]).subscribe(([videoElement, volume]: readonly [HTMLVideoElement, IVolumeControls]) => {
    videoElement.volume = volume.volume
    videoElement.muted = volume.isSelectedStream ? volume.isMuted : true
    this.volumeChange.next({ volume: volume.volume, isMuted: volume.isMuted })
  })

  public readonly setQualityLevel = combineLatest(this.videoLoaded$, this.qualityLevel$)
    .pipe(
      map(([videoElement, qualityLevel]) => ({ videoElement, qualityLevel })),
      filter(res => res.qualityLevel.isSelectedStream),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(qualityObj => {
      this.maybeHlsInstance().tapSome(hls => (hls.currentLevel = qualityObj.qualityLevel.quality))
    })

  public readonly toggleVideoPlaying = combineLatest(this.videoLoaded$, this.playToggle$)
    .pipe(
      map(([videoElement, playToggle]) => ({ videoElement, playToggle })),
      filter(res => res.playToggle.isSelectedStream),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(playingVideo => {
      playingVideo.playToggle.isPlaying ? playingVideo.videoElement.pause() : playingVideo.videoElement.play()
    })

  public readonly authenticationToken$ = this.hls$.pipe(
    map(response => response.success),
    filter<any>(Boolean),
    map(success => success.meta),
    filter<any>(Boolean),
    map(meta => meta.token)
  )

  /**
   * When a new token comes in it means the player has been recovered/refreshed
   * in this case we destroy the video-ads component from the dom and reload it
   * to prevent odd ad loading ad request events
   */
  public newTokenRequested$ = this.authenticationToken$.pipe(
    distinctUntilChanged(),
    switchMapTo(timer(1000, this.scheduler).pipe(mapTo(true), startWith(false), takeUntil(this.ngOnDestroy$)))
  )

  emitQualityOnVideoSelect = combineLatest(this.isSelectedVideo$, this.videoLoaded$)
    .pipe(
      map(([selectedVideo, videoElement]) => ({ selectedVideo, videoElement })),
      filter(res => res.selectedVideo),
      tap(res => {
        this.maybeHlsInstance()
          .filter(hls => hls.levels !== undefined)
          .tapSome(hls => {
            const hlsQualityData: any = {
              levels: getHlsQualityLevels(hls.levels),
              manualLevel: (hls as any).manualLevel
            }
            // sends the selectedVideos current hls qualityLevels along with the selected quality to
            // quality component. Also sends the selectedVideos playState, if its paused or playing,
            // and the selectedVideos duration.
            if (hlsQualityData) {
              this.hlsLoaded.next(hlsQualityData)
            }
            this.playState.next({ isPaused: res.videoElement.paused })
            this.videoDuration.next(res.videoElement.duration)
          })
      }),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe()

  private readonly adComplete$ = this.adEvent$.pipe(
    filter(adEvent => adEvent === 'adComplete' || adEvent === 'adError')
  )

  private adStart$ = this.adEvent$.pipe(filter(adEvent => adEvent === 'adStarted'))

  public isAdPlaying$ = combineLatest(
    this.isSelectedVideo$,
    merge(
      this.adStart$.pipe(map(() => true)),
      this.adComplete$.pipe(map(() => false)),
      this.ads.isAnAdBlockerActive().pipe(
        filter(adBlockerIsActive => adBlockerIsActive === true),
        map(() => false)
      )
    )
  )
    .pipe(
      map(([isSelectedVideo, adPlaying]) => isSelectedVideo && adPlaying),
      startWith(false),
      shareReplay(1),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(isAdPlaying => this.adPlaying.next(isAdPlaying))

  private readonly playerId = `video-player-random-id-${Math.ceil(Math.random() * 111111)}`

  public changeResolution$ = combineLatest(this.hlsManifestParsedEvent, this.hlsLevelSwitchedEvent).pipe(
    mapGetResolution
  )

  public videoAdLogPartial$ = combineLatest(this.changeResolution$, this.videoAnalyticsData$).pipe(
    map(([resolution, videoAnalyticsData]) => ({
      type: 'ad',
      context: {
        playerInstance: this.id,
        referer: this.window?.location?.href,
        playerId: this.playerId,
        appVersion: 'v3.0',
        file: this.maybeVideoElement()
          .map(a => a.src)
          .valueOrUndefined(),
        appName: 'FloSports WebApp 3.0', // TODO: automate/inject this
        pluginName: 'hls.js', // TODO: automate/inject this
        pluginVersion: '0.13.2' // TODO: automate/inject this
      },
      user: {
        id: videoAnalyticsData.username,
        resolution
      }
    })),
    shareReplay(1)
  )

  public updateRendition = this.videoLoaded$
    .pipe(
      switchMap(contentVideoElement => fromEvent(contentVideoElement, 'resize').pipe(map(() => contentVideoElement))),
      withLatestFrom(this.hlsInstance$),
      map(([video, hls]) => {
        const _hls = hls.valueOrUndefined()
        const bitrate = _hls ? getBitrateFromHls(_hls) : undefined
        return buildRenditionString(video.videoWidth, video.videoHeight, bitrate)
      }),
      withLatestFrom(this.analyticsPlugin$),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(([rendition, analyticsPlugin]) => {
      analyticsPlugin.setOptions({ 'content.rendition': rendition })
    })

  public updateAnalyticsPluginState = fromEvent(this.doc, 'visibilitychange')
    .pipe(
      withLatestFrom(this.analyticsPlugin$),
      map(([event, analyticsPlugin]: [Event, any]) => [event, analyticsPlugin]),
      takeUntil(this.ngOnDestroy$)
    )
    .subscribe(([event, analyticsPlugin]) => {
      if (analyticsPlugin.deviceDetector?.isSmartphone()) {
        event.target.visibilityState === 'hidden' ? analyticsPlugin.disable() : analyticsPlugin.enable()
      }
    })

  public maybeVideoElement = () =>
    maybe(this.videoElement)
      .flatMapAuto(a => a.first)
      .flatMapAuto(a => a.nativeElement)
  private maybeHlsInstance = () => this.hlsInstanceSource.getValue()

  mseClientChanged(evt: Hls) {
    this.hlsInstanceSource.next(maybe(evt))
  }

  stopSpinner() {
    this.spinnerSource.next(false)
  }

  startSpinner() {
    this.spinnerSource.next(true)
  }

  cleanupVideo() {
    this.pausePreviewTracking()
    this.dispatchCustomEventSafely('suspend')
    this.prepareAndRemoveListeners()
  }

  runRecover = () =>
    this.hlsLoader(this.videoReferenceId, this.isFreeStream, this.videoMeta).pipe(
      delay(1000), // retry every 1 seconds
      takeWhile(() => this.fatalRetryCounterSource.getValue() < this.MAX_HLS_FATAL_RETRY_CYCLES), // max tries
      tap(() => this.fatalRetryCounterSource.next(this.fatalRetryCounterSource.getValue() + 1)),
      tap(hlsLoader => this.hlsSource.next(hlsLoader)),
      takeUntil(this.ngOnDestroy$)
    )

  clickToPlayBtn() {
    this.showingClickToPlayBtnSource.next(false)
    this.maybeVideoElement().tapSome(v => v.play())
  }

  ngOnDestroy() {
    this.cleanupVideo()
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.volumeControls && changes.volumeControls.currentValue) {
      this.volumeSource.next(changes.volumeControls.currentValue)
    }

    maybe(changes.videoSkipForward)
      .flatMapAuto(videoSkipForward => videoSkipForward.currentValue)
      .tapSome(videoSkipForward => this.videoSkipForwardSource.next(videoSkipForward))

    maybe(changes.videoSkipBackward)
      .flatMapAuto(videoSkipBackward => videoSkipBackward.currentValue)
      .tapSome(videoSkipBackward => this.videoSkipBackwardSource.next(videoSkipBackward))

    maybe(changes.pauseVideoOnSeeking)
      .flatMapAuto(isSeeking => isSeeking.currentValue)
      .tapSome(isSeeking => this.isSeekingSource.next(isSeeking))

    maybe(changes.videoPositionChange)
      .flatMapAuto(videoPosition => videoPosition.currentValue)
      .tapSome(videoPosition => this.videoPositionSource.next(videoPosition))

    maybe(changes.qualityLevel)
      .flatMapAuto(qualityLevel => qualityLevel.currentValue)
      .tapSome(qualityLevel => this.qualitySource.next(qualityLevel))

    maybe(changes.playToggle)
      .flatMapAuto(playToggle => playToggle.currentValue)
      .tapSome(playToggle => this.playToggleSource.next(playToggle.isPaused))

    maybe(changes.isSelectedVideo)
      .flatMapAuto<boolean>(change => change.currentValue)
      .tapSome(val => this.isSelectedVideoSource.next(val))

    maybe(changes.isFreeStream)
      .flatMapAuto<boolean>(change => change.currentValue)
      .tapSome(val => this.isFreeStreamSource.next(val))

    maybe(changes.videoReferenceId)
      .flatMap(change =>
        change.previousValue === change.currentValue ? maybe<string>() : maybe<string>(change.currentValue)
      )
      .tapSome(videoReferenceId => {
        this.startSpinner()
        this.videoReferenceIdSource.next(videoReferenceId)
        this.pausePreviewTracking()
        this.dispatchCustomEventSafely('suspend')
      })

    maybe(changes.error)
      .flatMap(change =>
        change.previousValue === change.currentValue
          ? maybe<ErrorInterface>()
          : maybe<ErrorInterface>(change.currentValue)
      )
      .tapSome(err => {
        this.errorSource.next(err)
        this.stopSpinner()
      })
  }

  ngAfterViewInit() {
    this.errorSource.next(this.error)
    maybe(this.videoElement).tapSome(queryList => {
      combineLatest([this.hlsInstance$, queryList.changes.pipe(take(1))])
        .pipe(takeUntil(this.ngOnDestroy$))
        .subscribe(([hlsInstance, _qlUntyped]) => {
          const emitFn = () =>
            maybe(queryList.first)
              .map(a => a.nativeElement)
              .tapSome(elm => {
                this.videoLoaded.next(elm)
                this.startSpinner()
              })
          hlsInstance.tap({
            none: emitFn,
            some: hls => hls.on(Hls.Events.MANIFEST_PARSED, emitFn)
          })
        })
    })
  }

  showClickToPlayBtn() {
    this.showingClickToPlayBtnSource.next(true)
  }

  hideClickToPlayBtn() {
    this.showingClickToPlayBtnSource.next(false)
  }

  // acting on error messages, level switching, and manifest parsed events
  handleHlsMessages(evt?: any) {
    maybe(evt)
      .filter(e => e.key === Hls.Events.ERROR)
      .flatMapAuto(a => a.message)
      .filter(msg => msg.fatal)
      .tapSome(msg => {
        this.hideClickToPlayBtn()
        const maybeNetworkDetails = maybe(msg.networkDetails)

        const code =
          this.fatalRetryCounterSource.getValue() < this.MAX_HLS_FATAL_RETRY_CYCLES
            ? VideoPlayerScreenErrorCode.FATAL_HLS
            : mapServiceErrorsToVideoErrors(maybeNetworkDetails.map(a => a.status).valueOrUndefined())

        const errorToSend = {
          fatal: true,
          type: msg.type,
          errName: 'FATAL HLS.JS ERROR',
          code,
          message: maybeNetworkDetails
            .flatMapAuto(a => a.statusText)
            .filter(txt => txt !== '')
            .valueOr(msg.details)
        }

        this.dispatchCustomEventSafely('error', errorToSend)
        this.errorSource.next(errorToSend)
      })
    maybe(evt)
      .filter(e => e.key === Hls.Events.ERROR)
      .flatMapAuto(a => a.message)
      .filter(msg => msg.details === 'fragLoadError')
      .tapSome(() => {
        this.hideClickToPlayBtn()
      })

    maybe(evt)
      .filter(e => e.key === Hls.Events.LEVEL_SWITCHED)
      .flatMapAuto(a => a.message)
      .tapSome(levelSwitched => this.hlsLevelSwitchedEvent.next(levelSwitched))

    maybe(evt)
      .filter(e => e.key === Hls.Events.MANIFEST_PARSED)
      .flatMapAuto(a => a.message)
      .tapSome(manifestParsed => this.hlsManifestParsedEvent.next(manifestParsed))
  }

  private pausePreviewTracking() {
    this.previewInteruptSource.next()
  }

  doIfMarkedVideoElementIsReady(f: (ve: HTMLVideoElement) => void) {
    this.maybeVideoElement().tap({
      none: () => undefined,
      some: el => setTimeout(() => f(el))
    })
  }

  dispatchCustomEventSafely(type: string, detail?: any) {
    this.maybeVideoElement().tap({
      none: () => undefined,
      some: el => dispatchCustomEvent(this.doc, type, el, detail)
    })
  }

  prepareAndAddListeners<T>(hlsFactoryMeta?: T) {
    this.doIfMarkedVideoElementIsReady(ve => {
      this.addListeners(
        ve,
        this.id,
        this.groupId,
        this.listeners,
        this.listRefMap,
        this.maybeHlsInstance().match({
          none: () => this.videoMeta || {},
          some: hlsInstance => ({ hlsInstance, ...(this.videoMeta || {}) })
        }),
        hlsFactoryMeta
      )
    })
  }

  prepareAndRemoveListeners() {
    this.doIfMarkedVideoElementIsReady(ve => {
      this.unloadListeners(ve, this.listeners, this.listRefMap)
    })
  }

  errorEmitterFn(code: number, message?: string) {
    this.errorSource.next({
      message,
      code
    })
  }

  addListeners(
    videoElement: HTMLVideoElement,
    videoPlayerInstanceId: string,
    videoGroupId: string,
    listeners: ListenerDictionary,
    listenerRefs: any,
    eventMeta?: object,
    hlsLoaderMeta?: any
  ) {
    const merged: ReadonlyArray<ListenerDictionary> = [...(this.videoEventBindings || []), listeners]

    // TODO: this should only have 1 side-effect iterator fn
    merged.forEach((a: any, idx) => {
      Object.keys(a)
        .filter(isFunction)
        .forEach(key => {
          const refKey = `${key}_${idx}`
          const listener: CustomEventHandler<Event, any, any> = (evt: Event) =>
            ((ve: HTMLVideoElement, playerId: string, playerGroupId: string, hlsMeta: any, meta: any, emitErrorFn) => {
              a[key](evt, ve, playerId, playerGroupId, meta, hlsMeta, emitErrorFn)
            })(
              videoElement,
              videoPlayerInstanceId,
              videoGroupId,
              eventMeta,
              hlsLoaderMeta,
              this.errorEmitterFn.bind(this)
            )
          listenerRefs[refKey] = listener
          videoElement.addEventListener(key, listener as any, { passive: true })
        })
    })
  }

  unloadListeners(videoElement: HTMLVideoElement, listeners: ListenerDictionary, listenerRefs: any) {
    const merged: ReadonlyArray<ListenerDictionary> = [...(this.videoEventBindings || []), listeners]
    // TODO: this should only have 1 side-effect iterator fn
    merged.forEach((a, idx) => {
      Object.keys(a)
        .filter(isFunction)
        .forEach(key => {
          videoElement.removeEventListener(key, listenerRefs[`${key}_${idx}`])
        })
    })
  }
}
